import { chain, update, set, reduce, forEach } from "lodash"
import { generateGathererKeys } from "connected-components/dashboard/helpers/query-key"
import { buildContributorKey } from "helpers/contributors"
import { getContributorLabel, NOT_HELD } from "connected-components/asset-view/helpers"
import { filterOptionsByCategory, getFactorValueByMetricDisplay } from "./helpers/factor-utils"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet } from "../helpers"

const CONTRIBUTORS_SUBQUERY = `
contributors {
  id
  description
  modelProviderId
  assetClass
  notHeld
  factors {
    id
    category
    netContribution
    currencyExposure
    securityExposure
  }
}`

function getUngroupedContributorsSubQuery(shouldUseIntradayQuery) {
  return `exposureContributors (contentSetId: $contentSetId ${shouldUseIntradayQuery ? "" : ", includeNotHeld: $includeNotHeld"}) {
    ${CONTRIBUTORS_SUBQUERY}
  }`
}

function getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) {
  return `groupedExposureContributors (${getGroupBySubqueryParameters(groupByFields)}, contentSetId: $contentSetId ${shouldUseIntradayQuery ? "" : ", includeNotHeld: $includeNotHeld"}) {
    groupedContributors {
      id
      name
      factors {
        id
        category
        netContribution
        currencyExposure
        netExposure
      }
      ${CONTRIBUTORS_SUBQUERY}
    }
  }`
}

function getQuery(groupByFields, shouldUseIntradayQuery) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""

  const innerQuery = `
    exposure (contentSetId: $contentSetId) {
      factors {
        id
        category
        netContribution: net
        currencyExposure
      }
    }
    ${groupByFields ? getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) : getUngroupedContributorsSubQuery(shouldUseIntradayQuery)}
  `

  if (shouldUseIntradayQuery) {
    return `query ContentSetContributorsQuery($modelId: String!, $on: Date!, $batchId: Id, $positionSet: PositionSetInput!, $contentSetId: String!${extraParameters}) {
      model (id: $modelId) {
        simulation: intradaySimulationBatch (
          positionSet: $positionSet
          on: $on
          batchId: $batchId
        ) {
          ${innerQuery}
        }
      }
    }`
  }

  return `query ContentSetContributorsQuery($modelId: String!, $date: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean, $positionSet: PositionSetInput!, $contentSetId: String!${extraParameters}) {
    model (id: $modelId) {
      simulation (
        positionSet: $positionSet
        from: $date
        to: $date
        base: $base
        interval: AUTO
      ) {
        ${innerQuery}
      }
    }
  }`
}

function getContributorFactorValues(contributor, options) {
  const { category, selectedMetricDisplay } = options

  if (!contributor?.factors) {
    return {}
  }

  if (!category) {
    return reduce(contributor.factors, (acc, factor) => {
      acc[factor.id] = selectedMetricDisplay ? getFactorValueByMetricDisplay(factor, selectedMetricDisplay) : factor
      return acc
    }, {})
  }

  const filteredFactors = filterOptionsByCategory(contributor.factors, category)

  return reduce(filteredFactors, (acc, factor) => {
    const value = getFactorValueByMetricDisplay(factor, selectedMetricDisplay)
    set(acc, [factor.category, factor.id], value)
    return acc
  }, {})
}

function updateNotHeldFactorMetrics(acc, factor, prefix, selectedMetricDisplay) {
  if (!selectedMetricDisplay) {
    const metrics = ["netContribution", "currencyExposure", "securityExposure", "netExposure"]
    metrics.forEach(metric => {
      update(acc, [NOT_HELD, "values", prefix, factor.id, metric], (value) => (value || 0) + (factor[metric] || 0))
    })
  } else {
    const factorValue = getFactorValueByMetricDisplay(factor, selectedMetricDisplay)
    update(acc, [NOT_HELD, "values", prefix, factor.category, factor.id], (value) => (value || 0) + factorValue)
  }
}

function processNotHeldContributor(acc, contributor, prefix, options) {
  if (!acc[NOT_HELD]) {
    acc[NOT_HELD] = {
      label: "Not Held",
      notHeld: true,
      values: {
        [prefix]: {},
      },
    }
  }

  const filteredFactors = filterOptionsByCategory(contributor.factors, options.category)

  forEach(filteredFactors, (factor) => {
    updateNotHeldFactorMetrics(acc, factor, prefix, options.selectedMetricDisplay)
  })
}

function parseContributors(prefix, contributors, options) {
  if (!contributors) {
    return {}
  }

  return reduce(contributors, (acc, contributor) => {
    if (contributor.notHeld && options.notHeldRollup) {
      processNotHeldContributor(acc, contributor, prefix, options)
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld,
        values: {
          [prefix]: getContributorFactorValues(contributor, options),
        },
      }
    }
    return acc
  }, {})
}

function parseGroupedContributors(prefix, groups, options) {
  if (!groups) {
    return {}
  }

  return chain(groups)
    .keyBy("id")
    .mapValues(group => ({
      label: group.name,
      values: {
        [prefix]: getContributorFactorValues(group, options),
      },
      children: parseContributors(prefix, group.contributors, options),
    }))
    .value()
}

export function createContentSetContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, contentSetId, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { batchId, on, shouldUseIntradayQuery } = options

  const additionalVariables = shouldUseIntradayQuery
    ? { on, batchId }
    : { base, date: to, includeNotHeld: !!base }

  const { key, graphqlKey } = generateGathererKeys("content-set-exposure.contributors", {
    prefix, modelId, contentSetId, positionSet,
    groupBy, groupById, tier,
    intraday: shouldUseIntradayQuery || undefined,
    ...(shouldUseIntradayQuery ? { batchId, on } : { base, to }),
  })

  return {
    prefix,
    key,
    graphqlKey,
    query: getQuery(groupBy && groupByFields, shouldUseIntradayQuery),
    variables: {
      modelId,
      contentSetId,
      positionSet: sanitizedPositionSet,
      ...groupByFields,
      ...additionalVariables,
    },
    select: (data) => {
      const sim = data.data.model.simulation
      const total = getContributorFactorValues(sim.exposure?.[0], options)

      const children = groupBy
        ? parseGroupedContributors(prefix, sim.groupedExposureContributors?.[0]?.groupedContributors, options)
        : parseContributors(prefix, sim.exposureContributors?.[0]?.contributors, options)

      return {
        label: "Total",
        values: {
          [prefix]: total,
        },
        children,
      }
    },
  }
}
